import { createApiError, createServerError, SERVER_ERROR_MESSAGE } from "./apiError";
import { triggerAuthError } from "../auth/authErrorHandler";
import { formatErrorForDisplay } from "@/utils/errorMessage";
import { organizationForApi } from "../utils/organizationScoping";
import { resolveApiBaseUrl } from "./apiBase";

// REST API service for items
const API_URL = resolveApiBaseUrl();

function withOrganizationQuery(path: string, organization?: string | null): string {
    const o = organizationForApi(organization);
    if (!o) return path;
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}organization=${encodeURIComponent(o)}`;
}

export type FetchItemsOptions = {
    aclAction?: 'list' | 'relate';
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
    filters?: Record<string, string | string[]>;
    /** Column filter UI state (text/number/boolean/select/date ranges) for server-side list filtering */
    columnFilters?: Record<string, unknown>;
    /** Omit or `no`: exclude archived; `all`: include; `only`: archived rows only */
    archived?: 'all' | 'no' | 'only';
    /** Scoped collection resolution (org collections) */
    organization?: string;
};

export type FetchItemsResult = {
    items: any[];
    totalCount?: number;
};


// Helper function to check for Firebase audience mismatch and clear invalid tokens
const checkAndHandleFirebaseAudienceError = (errorText: string): boolean => {
    const errorLower = errorText.toLowerCase();
    if (errorLower.includes('incorrect "aud"') || errorLower.includes('audience') || errorLower.includes('demo-project')) {
        console.error('❌ ItemsAPI: Firebase project mismatch detected. Clearing invalid token.');
        console.error('   This usually means the token was generated with a different Firebase project.');
        console.error('   Please sign in again to get a new token.');
        console.trace('🔍 ItemsAPI: Token cleared due to Firebase audience mismatch');
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        return true;
    }
    return false;
};

// Track token state to debug clearing issues
let tokenCheckCount = 0;

// Get auth token from localStorage
const getAuthToken = (): string | null => {
    tokenCheckCount++;
    const token = localStorage.getItem('idToken');

    if (!token && tokenCheckCount <= 3) {
        // Only log the first few times to avoid spam
        console.warn(`⚠️ ItemsAPI: getAuthToken() called (${tokenCheckCount}x) but token is missing`);
        if (tokenCheckCount === 1) {
            console.trace('🔍 ItemsAPI: First token check - token missing');
        }
    }

    // Trim any whitespace that might have been accidentally added
    return token ? token.trim() : null;
};

// Headers for API requests
const getHeaders = (includeContentType: boolean = true) => {
    const headers: Record<string, string> = {};

    // Only add Content-Type for requests with body (POST, PUT, etc.)
    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    // Add Bearer token from localStorage
    // Check localStorage directly first to debug
    const rawToken = localStorage.getItem('idToken');
    const token = getAuthToken();

    if (!token && rawToken) {
        console.error('❌ ItemsAPI: Token exists in localStorage but getAuthToken() returned null!');
        console.error('❌ ItemsAPI: Raw token length:', rawToken.length);
        console.error('❌ ItemsAPI: Raw token preview:', rawToken.substring(0, 20) + '...');
    }

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        // Log token info for debugging (masked)
        const tokenPreview = token.length > 20 ? `${token.substring(0, 10)}...${token.substring(token.length - 10)}` : '***';
        console.log(`🔑 ItemsAPI: Using token: ${tokenPreview} (length: ${token.length})`);
    } else {
        console.warn('⚠️ ItemsAPI: No token found in localStorage - request will be unauthenticated');
        console.warn('⚠️ ItemsAPI: Raw localStorage check:', rawToken ? `Found (${rawToken.length} chars)` : 'NOT FOUND');
    }

    // Log final headers for debugging (without exposing token)
    console.log(`📋 ItemsAPI: Request headers:`, {
        hasContentType: !!headers['Content-Type'],
        hasAuthorization: !!headers['Authorization'],
        headerKeys: Object.keys(headers),
        authHeaderValue: headers['Authorization'] ? `${headers['Authorization'].substring(0, 20)}...` : 'MISSING'
    });

    return headers;
};

export const fetchItemByIdAPI = async (slug: string, uid: string, organization?: string | null): Promise<any> => {
    const safeSlug = typeof slug === 'string' ? slug.replace(/[!]+$/, '').trim() : String(slug);
    const safeUid = typeof uid === 'string' ? uid.trim() : String(uid);
    if (!safeSlug || !safeUid) {
        throw new Error('Missing slug or uid');
    }
    const path = `/collections/${safeSlug}/items/${encodeURIComponent(safeUid)}`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(false)
    });
    if (!response.ok) {
        const text = await response.text().catch(() => '');
        if (checkAndHandleFirebaseAudienceError(text)) {
            triggerAuthError();
        }
        throw createApiError(
            (text || response.statusText || SERVER_ERROR_MESSAGE).trim() || SERVER_ERROR_MESSAGE,
            response.status
        );
    }
    const result = await response.json();
    if (result && typeof result === 'object') {
        if ('item' in result) return (result as any).item;
        if ('data' in result) return (result as any).data;
    }
    return result;
};

// Create a new item via API
// Uses slug (e.g., 'bookings', 'inventory') to identify the component
// The backend resolves the slug to the appropriate collection based on component configuration
export const createItemAPI = async (
    slug: string,
    itemData: any = {},
    organization?: string | null
): Promise<any> => {
    try {
        const path = `/collections/${slug}/items`;
        const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
        const response = await fetch(url, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(itemData)
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        const responseText = await response.text();

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        const data = JSON.parse(responseText);

        if (!data.success) {
            throw new Error(data.message || 'Failed to create item');
        }

        invalidateItemsCacheForSlug(slug);
        // API returns { success, message, item, dataSource }
        return data.item || data.data;

    } catch (error) {
        console.error('❌ Failed to create item via API:', error);
        throw error;
    }
};

// Update an item via API
// Uses slug (e.g., 'bookings', 'inventory') to identify the component
// The backend resolves the slug to the appropriate collection based on component configuration
// Note: PUT requests use 'objectId' field for the URL identifier. Falls back to 'uid' if objectId is not available.
export const updateItemAPI = async (
    slug: string,
    itemIdentifier: string,
    itemData: any,
    organization?: string | null
): Promise<any> => {
    try {
        const path = `/collections/${slug}/items/${itemIdentifier}`;
        const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
        const response = await fetch(url, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(itemData)
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        const responseText = await response.text();

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        const data = JSON.parse(responseText);

        if (!data.success) {
            throw new Error(data.message || 'Failed to update item');
        }

        invalidateItemsCacheForSlug(slug);
        // API returns { success, message, item, dataSource }
        return data.item || data.data;

    } catch (error) {
        console.error('❌ Failed to update item via API:', error);
        throw error;
    }
};

export const archiveItemAPI = async (
    slug: string,
    itemIdentifier: string,
    organization?: string | null
): Promise<any> => {
    const path = `/collections/${slug}/items/${encodeURIComponent(itemIdentifier)}/archive`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(false),
    });

    if (response.status === 401) {
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        throw new Error('Authentication required. Please login again.');
    }

    const responseText = await response.text();

    if (!response.ok) {
        if (response.status >= 500) {
            throw createServerError(response.status);
        }
        if (checkAndHandleFirebaseAudienceError(responseText)) {
            throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
        }
        let message = `HTTP ${response.status}`;
        try {
            const errJson = JSON.parse(responseText) as { message?: string };
            if (errJson?.message) {
                message = errJson.message;
            }
        } catch {
            if (responseText) {
                message = responseText;
            }
        }
        throw new Error(message);
    }

    const data = JSON.parse(responseText) as { success?: boolean; message?: string; item?: unknown };
    if (!data.success) {
        throw new Error(data.message || 'Failed to archive item');
    }

    invalidateItemsCacheForSlug(slug, organization ?? undefined);
    return data.item;
};

export const restoreItemAPI = async (
    slug: string,
    itemIdentifier: string,
    organization?: string | null
): Promise<any> => {
    const path = `/collections/${slug}/items/${encodeURIComponent(itemIdentifier)}/restore`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(false),
    });

    if (response.status === 401) {
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        throw new Error('Authentication required. Please login again.');
    }

    const responseText = await response.text();

    if (!response.ok) {
        if (response.status >= 500) {
            throw createServerError(response.status);
        }
        if (checkAndHandleFirebaseAudienceError(responseText)) {
            throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
        }
        let message = `HTTP ${response.status}`;
        try {
            const errJson = JSON.parse(responseText) as { message?: string };
            if (errJson?.message) {
                message = errJson.message;
            }
        } catch {
            if (responseText) {
                message = responseText;
            }
        }
        throw new Error(message);
    }

    const data = JSON.parse(responseText) as { success?: boolean; message?: string; item?: unknown };
    if (!data.success) {
        throw new Error(data.message || 'Failed to restore item');
    }

    invalidateItemsCacheForSlug(slug, organization ?? undefined);
    return data.item;
};

export type PickAndInformResult = {
    emailSent?: boolean;
    emailSkipped?: boolean;
    emailError?: string;
    message?: string;
};

export const pickAndInformSubmissionsAPI = async (
    slug: string,
    keepUid: string,
    archiveUidOrUids: string | string[],
    organization?: string | null,
    personalNote?: string | null
): Promise<PickAndInformResult> => {
    const path = `/collections/${slug}/items/pick-and-inform`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const rawList = Array.isArray(archiveUidOrUids) ? archiveUidOrUids : [archiveUidOrUids];
    const keep = String(keepUid).trim();
    const seen = new Set<string>();
    const archiveUids = rawList
        .map((id) => String(id).trim())
        .filter((id) => {
            if (!id || id === keep || seen.has(id)) {
                return false;
            }
            seen.add(id);
            return true;
        });
    const note = typeof personalNote === 'string' ? personalNote.trim() : '';
    const body: { keepUid: string; archiveUids: string[]; personalNote?: string } = {
        keepUid: keep,
        archiveUids,
    };
    if (note) {
        body.personalNote = note;
    }
    const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
    });

    if (response.status === 401) {
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        throw new Error('Authentication required. Please login again.');
    }

    const responseText = await response.text();
    if (!response.ok) {
        if (response.status >= 500) {
            throw createServerError(response.status);
        }
        if (checkAndHandleFirebaseAudienceError(responseText)) {
            throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
        }
        let message = `HTTP ${response.status}`;
        try {
            const errJson = JSON.parse(responseText) as { message?: string };
            if (errJson?.message) {
                message = errJson.message;
            }
        } catch {
            if (responseText) {
                message = responseText;
            }
        }
        throw new Error(message);
    }

    const data = JSON.parse(responseText) as {
        success?: boolean;
        message?: string;
        emailSent?: boolean;
        emailSkipped?: boolean;
        emailError?: string;
    };
    if (!data.success) {
        throw new Error(data.message || 'Pick and inform failed');
    }
    invalidateItemsCacheForSlug(slug);
    return {
        emailSent: data.emailSent,
        emailSkipped: data.emailSkipped,
        emailError: data.emailError,
        message: data.message,
    };
};

// Delete an item via API
// Uses slug (e.g., 'bookings', 'inventory') to identify the component
// The backend resolves the slug to the appropriate collection based on component configuration
// Note: Items use 'uid' field, not 'id'. The parameter is named 'itemUid' for clarity.
export const deleteItemAPI = async (slug: string, itemUid: string, organization?: string | null): Promise<void> => {
    try {
        const path = `/collections/${slug}/items/${itemUid}`;
        const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
        const response = await fetch(url, {
            method: 'DELETE',
            headers: getHeaders(false) // DELETE requests don't need Content-Type
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        const responseText = await response.text();

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        const data = JSON.parse(responseText);

        if (!data.success) {
            throw new Error(data.message || 'Failed to delete item');
        }

        invalidateItemsCacheForSlug(slug);
    } catch (error) {
        console.error('❌ Failed to delete item via API:', error);
        throw error;
    }
};

export const delegateItemOwnerByEmailAPI = async (
    slug: string,
    itemUid: string,
    email: string,
    organization?: string | null
): Promise<unknown> => {
    const path = `/collections/${slug}/items/${itemUid}/owner`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const response = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(true),
        body: JSON.stringify({ email }),
    });
    const responseText = await response.text();
    if (response.status === 401) {
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        throw new Error('Authentication required. Please login again.');
    }
    if (!response.ok) {
        if (response.status >= 500) {
            throw createServerError(response.status);
        }
        if (checkAndHandleFirebaseAudienceError(responseText)) {
            throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
        }
        let message = `HTTP ${response.status}`;
        try {
            const errJson = JSON.parse(responseText) as { message?: string };
            if (errJson?.message) {
                message = errJson.message;
            }
        } catch {
            if (responseText) {
                message = responseText;
            }
        }
        throw new Error(message);
    }
    const data = JSON.parse(responseText) as { success?: boolean; message?: string; item?: unknown };
    if (!data.success) {
        throw new Error(data.message || 'Failed to delegate item owner');
    }
    invalidateItemsCacheForSlug(slug);
    return data.item;
};

export const clearDelegatedItemOwnerAPI = async (
    slug: string,
    itemUid: string,
    organization?: string | null
): Promise<unknown> => {
    const path = `/collections/${slug}/items/${itemUid}/owner`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(false),
    });
    const responseText = await response.text();
    if (response.status === 401) {
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        throw new Error('Authentication required. Please login again.');
    }
    if (!response.ok) {
        if (response.status >= 500) {
            throw createServerError(response.status);
        }
        if (checkAndHandleFirebaseAudienceError(responseText)) {
            throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
        }
        let message = `HTTP ${response.status}`;
        try {
            const errJson = JSON.parse(responseText) as { message?: string };
            if (errJson?.message) {
                message = errJson.message;
            }
        } catch {
            if (responseText) {
                message = responseText;
            }
        }
        throw new Error(message);
    }
    const data = JSON.parse(responseText) as { success?: boolean; message?: string; item?: unknown };
    if (!data.success) {
        throw new Error(data.message || 'Failed to remove delegated owner');
    }
    invalidateItemsCacheForSlug(slug);
    return data.item;
};

// Shared cache for GET items to avoid duplicate requests (e.g. table relation cells)
const ITEMS_CACHE_TTL_MS = 60 * 1000; // 60 seconds for list display
const RELATION_ITEMS_CACHE_TTL_MS = 120 * 1000; // 2 minutes for relation options
const itemsCache: { key: string; result: FetchItemsResult; expiresAt: number }[] = [];

/** Prefix cache keys by signed-in viewer so ACL-filtered lists are not reused across users. */
function getItemsCacheViewerKey(): string {
    if (typeof localStorage === 'undefined') {
        return "";
    }
    try {
        const raw = localStorage.getItem('user');
        if (raw) {
            const parsed = JSON.parse(raw) as { uid?: string };
            if (parsed?.uid && String(parsed.uid).trim()) {
                return String(parsed.uid).trim();
            }
        }
    } catch {
        /* ignore */
    }
    const tok = localStorage.getItem('authToken') || localStorage.getItem('idToken');
    if (tok && tok.length > 16) {
        return `t:${tok.slice(0, 10)}:${tok.slice(-10)}`;
    }
    return "anon";
}

const getItemsCacheKey = (slug: string, options: FetchItemsOptions): string => {
    const filterKey =
        options.filters && Object.keys(options.filters).length > 0
            ? JSON.stringify(options.filters)
            : "";
    const columnFilterKey =
        options.columnFilters && Object.keys(options.columnFilters).length > 0
            ? JSON.stringify(options.columnFilters)
            : "";
    const orgKey = options.organization?.trim() ?? "";
    const archivedKey = options.archived ?? "";
    const sortKey = `${options.orderBy ?? ""}:${options.orderDirection ?? ""}`;
    const tail = `${slug}:${orgKey}:${options.aclAction ?? ""}:${options.page ?? ""}:${options.limit ?? ""}:${sortKey}:${archivedKey}:${filterKey}:${columnFilterKey}`;
    return `${getItemsCacheViewerKey()}|${tail}`;
};

function getCachedItems(key: string): FetchItemsResult | null {
    const now = Date.now();
    const entry = itemsCache.find((e) => e.key === key && e.expiresAt > now);
    if (entry) return entry.result;
    return null;
}
function setCachedItems(key: string, result: FetchItemsResult, ttlMs?: number): void {
    const now = Date.now();
    const expiresAt = now + (ttlMs ?? ITEMS_CACHE_TTL_MS);
    const existing = itemsCache.findIndex((e) => e.key === key);
    if (existing >= 0) itemsCache.splice(existing, 1);
    itemsCache.push({ key, result, expiresAt });
    if (itemsCache.length > 50) itemsCache.splice(0, itemsCache.length - 40);
}

// Clear all cached items, e.g. when switching users
export function clearItemsCache(): void {
    itemsCache.length = 0;
}

// Invalidate cache for a slug so the next fetch returns fresh data (e.g. after create/update/delete)
export function invalidateItemsCacheForSlug(slug: string, organization?: string | null): void {
    if (!slug) return;
    const orgTrim = organization != null && String(organization).trim() ? String(organization).trim() : null;
    for (let i = itemsCache.length - 1; i >= 0; i--) {
        const key = itemsCache[i].key;
        const pipe = key.indexOf('|');
        const tail = pipe >= 0 ? key.slice(pipe + 1) : key;
        if (!tail.startsWith(slug + ':')) continue;
        if (orgTrim == null) {
            itemsCache.splice(i, 1);
            continue;
        }
        const rest = tail.slice(slug.length + 1);
        const colon = rest.indexOf(':');
        const keyOrg = colon >= 0 ? rest.slice(0, colon) : rest;
        if (keyOrg === orgTrim) {
            itemsCache.splice(i, 1);
        }
    }
}

// Fetch all items from a collection via API
// Uses slug (e.g., 'bookings', 'inventory') to identify the component
// The backend resolves the slug to the appropriate collection based on component configuration
export const fetchItemsAPI = async (slug: string, options: FetchItemsOptions = {}): Promise<FetchItemsResult> => {
    const cacheKey = getItemsCacheKey(slug, options);
    const cached = getCachedItems(cacheKey);
    if (cached) return cached;

    try {
        // Check token state before making request
        const rawTokenCheck = localStorage.getItem('idToken');
        console.log('🔍 ItemsAPI: Pre-request token check:', {
            rawTokenExists: !!rawTokenCheck,
            rawTokenLength: rawTokenCheck?.length || 0,
            timestamp: new Date().toISOString()
        });

        // Get token first to ensure it exists
        const token = getAuthToken();
        if (!token) {
            console.error('❌ ItemsAPI: No token available for request!');
            console.error('❌ ItemsAPI: localStorage idToken:', rawTokenCheck ? `EXISTS (${rawTokenCheck.length} chars)` : 'MISSING');
            console.error('❌ ItemsAPI: This suggests the token was cleared before this request');
            console.trace('🔍 ItemsAPI: Stack trace for missing token');

            // Check if token was recently cleared by checking if user data exists
            const userData = localStorage.getItem('user');
            if (userData) {
                console.warn('⚠️ ItemsAPI: User data exists but token is missing - token may have been cleared');
            }

            triggerAuthError();
            throw new Error('Authentication token not found. Please login again.');
        }

        const params = new URLSearchParams();
        if (options.aclAction) params.set('aclAction', options.aclAction);
        if (options.page != null) params.set('page', String(options.page));
        if (options.limit != null) params.set('limit', String(options.limit));
        if (options.orderBy) params.set('orderBy', options.orderBy);
        if (options.orderDirection) params.set('orderDirection', options.orderDirection);
        if (options.archived && options.archived !== 'no') {
            params.set('archived', options.archived);
        }
        if (options.filters && Object.keys(options.filters).length > 0) {
            params.set('filters', JSON.stringify(options.filters));
        }
        if (options.columnFilters && Object.keys(options.columnFilters).length > 0) {
            params.set('columnFilters', JSON.stringify(options.columnFilters));
        }
        const orgParam = organizationForApi(options.organization);
        if (orgParam) {
            params.set('organization', orgParam);
        }
        const query = params.toString() ? `?${params.toString()}` : '';
        const url = API_URL ? `${API_URL}/collections/${slug}/items${query}` : `/collections/${slug}/items${query}`;
        const headers = getHeaders(false); // GET requests don't need Content-Type

        // Verify token is in headers
        if (!headers['Authorization']) {
            console.error('❌ ItemsAPI: Authorization header missing after getHeaders()!');
            console.error('❌ ItemsAPI: Token exists:', !!token);
            console.error('❌ ItemsAPI: Headers object:', headers);
            throw new Error('Failed to include authentication token in request');
        }

        // Log request details for debugging
        console.log(`🌐 ItemsAPI: Fetching ${url}`, {
            hasToken: !!token,
            tokenLength: token?.length || 0,
            hasAuthHeader: !!headers['Authorization'],
            headerKeys: Object.keys(headers),
            authHeaderPreview: headers['Authorization'] ? headers['Authorization'].substring(0, 30) + '...' : 'MISSING'
        });

        // Final verification before making the request
        if (!headers['Authorization']) {
            const lastChanceToken = localStorage.getItem('idToken');
            console.error('❌ ItemsAPI: CRITICAL - Authorization header missing right before fetch!');
            console.error('❌ ItemsAPI: Token in localStorage:', lastChanceToken ? `Found (${lastChanceToken.length} chars)` : 'MISSING');
            console.error('❌ ItemsAPI: Headers object:', JSON.stringify(headers, null, 2));

            // Try to add it directly as a last resort
            if (lastChanceToken) {
                headers['Authorization'] = `Bearer ${lastChanceToken.trim()}`;
                console.log('⚠️ ItemsAPI: Added Authorization header as last resort');
            } else {
                throw new Error('Authentication token not available for request');
            }
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: headers
        });

        if (response.status === 401) {
            console.error('❌ ItemsAPI: Received 401 Unauthorized response');
            console.error('❌ ItemsAPI: URL:', url);
            console.error('❌ ItemsAPI: Token was present in request:', !!token);
            console.error('❌ ItemsAPI: Authorization header was present:', !!headers['Authorization']);
            console.trace('🔍 ItemsAPI: Token cleared due to 401 response');
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        const responseText = await response.text();

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            if (response.status === 403) {
                const error: any = new Error('Permission denied. Access is not configured. Please contact an administrator.');
                error.status = response.status;
                error.statusText = responseText;
                throw error;
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            // Create error with status code for better error handling
            const error: any = new Error(`HTTP ${response.status}: ${responseText}`);
            error.status = response.status;
            error.statusText = responseText;
            throw error;
        }

        const data = JSON.parse(responseText);

        if (!data.success) {
            const text = formatErrorForDisplay(
                data.message != null ? { message: data.message } : 'Failed to fetch items'
            );
            throw new Error(text);
        }

        // API returns { success, items, count, totalCount?, component, dataSource }
        const items = Array.isArray(data.items) ? data.items : (Array.isArray(data.data) ? data.data : []);
        const result = {
            items,
            totalCount: typeof data.totalCount === 'number' ? data.totalCount : undefined
        };
        const ttl = options.aclAction === 'relate' ? RELATION_ITEMS_CACHE_TTL_MS : undefined;
        setCachedItems(cacheKey, result, ttl);
        return result;

    } catch (error) {
        console.error('❌ Failed to fetch items via API:', error);
        throw error;
    }
};

/** Find which 1-based list page contains `uid` under the same sort/filters as the table. */
export async function resolveItemListPage(
    slug: string,
    uid: string,
    options: Omit<FetchItemsOptions, 'page'>
): Promise<number | null> {
    const safeUid = String(uid ?? '').trim();
    if (!safeUid) return null;

    const limit = Math.max(1, options.limit ?? 25);
    let page = 1;

    while (page <= 500) {
        const result = await fetchItemsAPI(slug, { ...options, page, limit });
        const items = Array.isArray(result?.items) ? result.items : [];
        if (items.some((item) => String(item?.uid ?? '').trim() === safeUid)) {
            return page;
        }
        const total = result.totalCount ?? items.length;
        if (items.length < limit || page * limit >= total) {
            break;
        }
        page += 1;
    }

    return null;
}

export async function appendGpxTrackPointAPI(
    slug: string,
    itemId: string,
    body: { field: string; point: { lat: number; lon: number; ele?: number; time: string } },
    organization?: string | null
): Promise<{ pointCount: number }> {
    const path = `/collections/${encodeURIComponent(slug)}/items/${encodeURIComponent(itemId)}/gpx-append`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const response = await fetch(url, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(body),
    });
    const responseText = await response.text();
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
    }
    const data = JSON.parse(responseText);
    if (!data.success) {
        throw new Error(data.message || "Failed to append GPX point");
    }
    return { pointCount: data.pointCount ?? 0 };
}

export async function gpxSetRecordingAPI(
    slug: string,
    itemId: string,
    field: string,
    action: "start" | "stop",
    organization?: string | null
): Promise<any> {
    const path = `/collections/${encodeURIComponent(slug)}/items/${encodeURIComponent(itemId)}/gpx-${action}`;
    const url = withOrganizationQuery(API_URL ? `${API_URL}${path}` : path, organization);
    const response = await fetch(url, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ field }),
    });
    const responseText = await response.text();
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
    }
    const data = JSON.parse(responseText);
    if (!data.success) {
        throw new Error(data.message || `Failed to ${action} GPX recording`);
    }
    if (data.item) {
        invalidateItemsCacheForSlug(slug);
    }
    return data.item ?? null;
}